import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Divider,
} from "@mui/material";
import {
  SportsBaseball,
  TrendingUp,
  Psychology,
  EmojiEvents,
} from "@mui/icons-material";
import {
  BRAND_ASSETS,
  BRAND_COLORS,
  brandFullLogoImgSx,
} from "../utils/brandAssets";
import SeoHead from "../components/SeoHead";
import LeaguePhotoRow from "../components/marketing/LeaguePhotoRow";
import { aboutLeaguePhotos } from "../components/marketing/marketingPhotosConfig";
import { getPublicSeo } from "../utils/seoCatalog";

const About = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const stats = [
    { number: "20,000+", label: "Players", icon: <SportsBaseball /> },
    { number: "125", label: "Years of History", icon: <EmojiEvents /> },
    { number: "∞", label: "Possibilities", icon: <TrendingUp /> },
  ];

  const values = [
    {
      title: "Accessibility",
      description: "Making complex statistics understandable for everyone",
      icon: <Psychology sx={{ fontSize: 40, color: BRAND_COLORS.cyan }} />,
    },
    {
      title: "Engagement",
      description: "Transforming data into interactive, enjoyable experiences",
      icon: <TrendingUp sx={{ fontSize: 40, color: "secondary.main" }} />,
    },
    {
      title: "Storytelling",
      description: "Numbers that tell the stories behind the games",
      icon: <SportsBaseball sx={{ fontSize: 40, color: BRAND_COLORS.cyan }} />,
    },
  ];

  return (
    <>
      <SeoHead {...getPublicSeo("about")} />

      <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
        {/* Main Story Section */}
        <Box
          sx={{
            py: { xs: 6, md: 8 },
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Container maxWidth="lg">
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    mb: 4,
                    color: "text.primary",
                  }}
                >
                  The Story Behind Stats.fan
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 4,
                    lineHeight: 1.8,
                    color: "text.primary",
                    fontWeight: 400,
                  }}
                >
                  Sports are full of numbers — records, leaders, milestones, and
                  the endless "what if" comparisons that fuel every debate. But
                  too often, stats feel locked in spreadsheets or buried in
                  databases. Stats.fan was built to change that.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: "1.1rem",
                    lineHeight: 1.8,
                    color: "text.secondary",
                  }}
                >
                  I'm James Carvallyo, and I created Stats.fan with one goal: to
                  make sports statistics fun, engaging, and accessible for fans
                  everywhere.
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: { xs: "center", md: "flex-end" },
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <Box
                    component="img"
                    src={BRAND_ASSETS.fullLogo}
                    alt="Stats.fan Logo"
                    sx={brandFullLogoImgSx}
                  />
                </Box>
              </Grid>
            </Grid>
            <LeaguePhotoRow
              items={aboutLeaguePhotos}
              sx={{ mt: { xs: 5, md: 6 } }}
            />
          </Container>
        </Box>

        {/* Stats Section */}
        <Box
          sx={{
            py: { xs: 6, md: 8 },
            bgcolor: "background.paper",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Container maxWidth="lg">
            <Grid container spacing={4} justifyContent="center">
              {stats.map((stat, index) => (
                <Grid item xs={12} sm={4} key={index}>
                  <Card
                    sx={{
                      textAlign: "center",
                      p: 3,
                      height: "100%",
                      transition: "transform 0.3s ease, border-color 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        borderColor: BRAND_COLORS.cyan,
                      },
                    }}
                  >
                    <CardContent>
                      <Box sx={{ color: BRAND_COLORS.cyan, mb: 2 }}>
                        {stat.icon}
                      </Box>
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 700,
                          color: BRAND_COLORS.cyan,
                          mb: 1,
                        }}
                      >
                        {stat.number}
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{ color: "text.secondary", fontWeight: 500 }}
                      >
                        {stat.label}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Vision Section */}
        <Box
          sx={{
            py: { xs: 6, md: 8 },
            bgcolor: "background.default",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Container maxWidth="lg">
            <Box textAlign="center" sx={{ mb: 6 }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  color: "text.primary",
                }}
              >
                Our Vision
              </Typography>
            </Box>
            <Typography
              variant="h6"
              sx={{
                maxWidth: 900,
                mx: "auto",
                textAlign: "center",
                lineHeight: 1.8,
                color: "text.primary",
                mb: 6,
              }}
            >
              Stats.Fan covers baseball, basketball, and football — MLB, NBA,
              and NFL players brought to life with interactive charts, trivia,
              and dashboards. The vision is a home for fans of all games —
              where numbers turn into stories, and stats become something you
              can actually enjoy exploring.
            </Typography>
            <Box textAlign="center">
              <Typography
                variant="h5"
                sx={{
                  fontStyle: "italic",
                  color: BRAND_COLORS.cyan,
                  fontWeight: 500,
                  maxWidth: 800,
                  mx: "auto",
                }}
              >
                "Because whether it's baseball, basketball, football, or
                beyond, it's not just about the numbers — it's about the
                stories the numbers tell."
              </Typography>
            </Box>
          </Container>
        </Box>

        {/* Values Section */}
        <Box
          sx={{
            py: { xs: 6, md: 8 },
            bgcolor: "background.paper",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Container maxWidth="lg">
            <Box textAlign="center" sx={{ mb: 6 }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  color: "text.primary",
                }}
              >
                What We Believe
              </Typography>
            </Box>
            <Grid container spacing={4}>
              {values.map((value, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Card
                    sx={{
                      height: "100%",
                      p: 4,
                      textAlign: "center",
                      transition: "transform 0.3s ease, border-color 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        borderColor: BRAND_COLORS.cyan,
                      },
                    }}
                  >
                    <CardContent>
                      <Box sx={{ mb: 3 }}>{value.icon}</Box>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 600,
                          mb: 2,
                          color: "text.primary",
                        }}
                      >
                        {value.title}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: "text.secondary",
                          lineHeight: 1.6,
                        }}
                      >
                        {value.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Mission Section */}
        <Box
          sx={{
            py: { xs: 6, md: 8 },
            bgcolor: BRAND_COLORS.cyan,
            color: BRAND_COLORS.navy,
          }}
        >
          <Container maxWidth="lg">
            <Box textAlign="center">
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  mb: 4,
                }}
              >
                Our Mission
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  maxWidth: 800,
                  mx: "auto",
                  lineHeight: 1.6,
                  opacity: 0.9,
                  fontWeight: 300,
                }}
              >
                To transform how fans interact with sports statistics by making
                data visualization engaging, accessible, and meaningful.
              </Typography>
            </Box>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default About;
